import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MessageCircle } from "lucide-react";
import { DRONES, COMPARADOR, FICHA_FUENTE, IMG, waCotizarDron, track } from "@/data/content";
import { Reveal, SectionTag, TituloLineas, FotoRevelada } from "@/components/Reveal";

const META_DESC = "Compare los tres DJI Agras — T100, T70P y T55 — pregunta por pregunta: litros, kilos, ancho de pasada, hectáreas por hora y baterías. Datos de campo reales de CERTERO SRL.";

const AGRAS = DRONES.filter((d) => d.id !== "mavic3m");

export default function CompararPage() {
  useEffect(() => {
    const prev = { t: document.title, d: document.querySelector('meta[name="description"]')?.content, c: document.querySelector('link[rel="canonical"]')?.href };
    document.title = "Comparar los Agras T100, T70P y T55 | CERTERO SRL";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.content = META_DESC;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = "https://certerosrl.com/drones/comparar";
    return () => {
      document.title = prev.t;
      if (meta && prev.d) meta.content = prev.d;
      if (link && prev.c) link.href = prev.c;
    };
  }, []);

  return (
    <div className="bg-abyss px-4 pb-24 pt-28 sm:px-8 lg:px-12 lg:pt-32">
      <div className="mx-auto max-w-[1400px]" data-testid="comparar-page">
        <nav aria-label="Ruta de navegación" className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke" data-testid="comparar-breadcrumb">
          <Link to="/" className="transition-colors duration-300 hover:text-certero">Inicio</Link>
          <span aria-hidden="true">›</span>
          <Link to="/#drones" className="transition-colors duration-300 hover:text-certero">Drones</Link>
          <span aria-hidden="true">›</span>
          <span className="text-certero">Comparar los Agras</span>
        </nav>

        {/* Portada */}
        <Reveal className="mt-8">
          <FotoRevelada
            src={IMG.agrasFumigacion}
            alt="Dron DJI Agras pulverizando sobre el cultivo — la línea Agras de CERTERO SRL"
            kenburns
            className="aspect-[16/9] w-full border border-hud"
            loading="eager"
          />
        </Reveal>

        <Reveal delay={0.06} className="mt-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">Línea Agras — frente a frente</p>
          <TituloLineas
            as="h1"
            lineas={["Compare los tres Agras", "con datos de campo"]}
            className="mt-3 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-mist sm:text-4xl lg:text-5xl"
          />
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-smoke sm:text-base">
            La misma plataforma en tres tamaños: elija por capacidad, cobertura y equipo.
          </p>
        </Reveal>

        {/* Accesos a cada ficha */}
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3" data-testid="comparar-tarjetas">
          {AGRAS.map((d, i) => (
            <Reveal key={d.id} delay={0.08 * i}>
              <Link
                data-testid={`comparar-btn-ficha-${d.slug}`}
                to={`/drones/${d.slug}`}
                onClick={() => track("ver_dron_click", { modelo: d.modelo, origen: "comparador" })}
                className="group flex h-full flex-col border border-hud bg-ink p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-certero/60"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-certero">{d.tag}</p>
                <h2 className="mt-3 font-display text-xl font-bold uppercase tracking-wide text-mist">{d.modelo}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-smoke">{d.paraQue}</p>
                <span className="mt-5 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.15em] text-certero transition-colors duration-300 group-hover:text-[#4bc443]">
                  Ver el {d.modelo.replace("DJI ", "")} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* Comparador completo */}
        <Reveal delay={0.1} className="mt-14">
          <div data-testid="comparador-agras" className="border border-hud bg-ink">
            <div className="border-b border-hud px-7 py-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-certero">Comparador en campo</p>
              <h2 className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl">
                Los tres Agras, pregunta por pregunta
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead>
                  <tr className="border-b border-hud bg-surface font-mono text-[10px] uppercase tracking-[0.15em] text-smoke">
                    <th className="p-4">Pregunta del productor</th>
                    {COMPARADOR.cols.map((c) => <th key={c} className="p-4 text-mist">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {COMPARADOR.filas.map((f) => (
                    <tr key={f.q} className="border-b border-hud/50">
                      <td className="p-4 text-mist/85">{f.q}</td>
                      {f.r.map((v, i) => (
                        <td key={i} className="p-4 font-mono text-xs text-mist/80">{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-hud px-7 py-4 font-mono text-[10px] leading-relaxed text-smoke/70">
              {FICHA_FUENTE}
            </p>
          </div>
        </Reveal>

        {/* Cierre */}
        <Reveal className="mt-14">
          <div className="flex flex-col items-start gap-6 border border-certero/40 bg-[#0c295c]/40 p-8 sm:p-10 lg:flex-row lg:items-center lg:justify-between" data-testid="comparar-cierre">
            <div>
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl">¿Cuál va con su cultivo?</h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-smoke">
                Cuéntenos su cultivo y su superficie: le recomendamos el equipo correcto, sin compromiso.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <a
                data-testid="comparar-btn-cotizar"
                href={waCotizarDron("Agras T100, T70P o T55")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("cotizar_dron_whatsapp", { modelo: "Agras (comparador)", origen: "pagina_comparar" })}
                className="inline-flex h-12 items-center gap-2 rounded-full bg-certero px-7 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] active:scale-95"
              >
                <MessageCircle className="h-4 w-4" /> Cotizar por WhatsApp
              </a>
              <Link
                data-testid="comparar-btn-volver"
                to="/#drones"
                className="group inline-flex h-12 items-center gap-2 rounded-full border border-certero px-6 font-display text-xs font-bold uppercase tracking-[0.15em] text-certero transition-[background-color,color] duration-300 hover:bg-certero hover:text-ink"
              >
                Ver los drones <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
